/**
 * OffersListPage
 * Lists all credit offers with search, filters, and click-to-detail navigation
 * Route: /credit/offers
 *
 * Vista agrupada por cliente: cada cliente aparece una vez con su oferta mas
 * reciente y un badge "x N" cuando hay historicas. Al expandir el grupo se
 * listan las anteriores como sub-filas indentadas. Por default se ocultan las
 * ofertas EXPIRED — se pueden incluir con el toggle "Incluir expiradas".
 */

import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { RefreshCw, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { OfferDetailModal } from '@/features/credit/components/OfferDetailModal';
import { useBackofficeOffers } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';

import { CreditLevel } from '@/types/credit.types';
import type { BackofficeOffersListItem, AvailableRate } from '@/types/backoffice-crud.types';
import { OFFER_STATUSES, CREDIT_LEVELS } from '@/types/backoffice-crud.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
  { label: 'Ofertas', path: ROUTES.OFFERS_LIST },
];

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'CONVERTED':
    case 'ACCEPTED':
      return 'success';
    case 'VIEWING':
      return 'info';
    case 'LOCKED':
      return 'warning';
    case 'EXPIRED':
      return 'danger';
    case 'ABANDONED':
      return 'default';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

// ─── Row Type ────────────────────────────────────────────────────────────────

/**
 * Row del listado agrupado. Un cliente con N ofertas produce 1 fila primaria
 * (isPrimary=true, groupCount=N, con la oferta MAS RECIENTE) + N-1 sub-filas
 * (isSubrow=true) que solo se renderizan cuando el grupo esta expandido.
 */
type OfferRow = BackofficeOffersListItem & {
  _rowId: string;
  _isPrimary: boolean;
  _isSubrow: boolean;
  _groupCount: number; // total de ofertas del cliente (post-filtro de estados)
  _directoryId: string;
};

// ─── Row helpers ─────────────────────────────────────────────────────────────

function formatRatesSummary(rates: AvailableRate[] | undefined): string {
  if (!rates || rates.length === 0) return '-';
  const terms = rates.map((r) => r.term_weeks).sort((a, b) => a - b);
  if (terms.length === 1) return `${terms[0]} sem`;
  return `${terms[0]}-${terms[terms.length - 1]} sem`;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Skeleton variant="rectangular" width="320px" height={40} />
        <Skeleton variant="rectangular" width="160px" height={40} />
        <Skeleton variant="rectangular" width="160px" height={40} />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={`row-skeleton-${i.toString()}`} variant="rectangular" height={44} />
        ))}
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function ListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar las ofertas
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function OffersListPage() {
  const { offers, isLoading, isError, refetch } = useBackofficeOffers();
  const [searchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState(searchParams.get('level') ?? '');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedOffer, setSelectedOffer] = useState<{
    offerId: string;
    directoryId: string;
  } | null>(null);

  // Filtrar por status + nivel. NO ocultamos EXPIRED por default: la
  // agrupacion por cliente + caret ▶ ya expone el historico. Si un cliente
  // solo tiene ofertas EXPIRED, aparece con su mas reciente como primary.
  const filteredOffers = useMemo(() => {
    let result = offers;
    if (statusFilter) {
      result = result.filter((o) => o.status === statusFilter);
    }
    if (levelFilter) {
      result = result.filter((o) => o.credit_level === levelFilter);
    }
    return result;
  }, [offers, statusFilter, levelFilter]);

  // Paso 2: agrupar por directory_id y armar las filas primarias (+ sub-filas
  // cuando el grupo esta expandido). Orden dentro del grupo: created_at DESC.
  // Orden de los grupos: por la fecha de la oferta mas reciente, DESC.
  const rows = useMemo<OfferRow[]>(() => {
    const groups = new Map<string, BackofficeOffersListItem[]>();
    for (const o of filteredOffers) {
      const key = o.directory_id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(o);
    }

    const primaries: OfferRow[] = [];
    for (const [directoryId, list] of groups) {
      list.sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
      const [head, ...rest] = list;
      if (!head) continue;
      const isExpanded = expandedGroups.has(directoryId);
      primaries.push({
        ...head,
        _rowId: `${directoryId}:head`,
        _isPrimary: true,
        _isSubrow: false,
        _groupCount: list.length,
        _directoryId: directoryId,
      });
      if (isExpanded) {
        for (const sub of rest) {
          primaries.push({
            ...sub,
            _rowId: `${directoryId}:${sub.offer_id}`,
            _isPrimary: false,
            _isSubrow: true,
            _groupCount: list.length,
            _directoryId: directoryId,
          });
        }
      }
    }

    // Ordenamos los grupos por la fecha de su primaria (created_at) desc.
    // Insertion order del Map = orden de aparicion; re-ordenamos preservando
    // el "bloque" primaria+subrows juntas.
    const blocks = new Map<string, OfferRow[]>();
    for (const r of primaries) {
      const key = r._directoryId;
      if (!blocks.has(key)) blocks.set(key, []);
      blocks.get(key)!.push(r);
    }
    const sortedKeys = Array.from(blocks.keys()).sort((a, b) => {
      const aHead = blocks.get(a)!.find((x) => x._isPrimary);
      const bHead = blocks.get(b)!.find((x) => x._isPrimary);
      return (bHead?.created_at ?? '').localeCompare(aHead?.created_at ?? '');
    });
    return sortedKeys.flatMap((k) => blocks.get(k)!);
  }, [filteredOffers, expandedGroups]);

  const toggleGroup = (directoryId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(directoryId)) next.delete(directoryId);
      else next.add(directoryId);
      return next;
    });
  };

  const columns = useMemo<ColumnDef<OfferRow, unknown>[]>(() => [
    {
      id: 'expand',
      header: '',
      cell: ({ row }) => {
        const r = row.original;
        if (!r._isPrimary || r._groupCount <= 1) {
          return <span className="inline-block w-4" aria-hidden />;
        }
        const isOpen = expandedGroups.has(r._directoryId);
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleGroup(r._directoryId);
            }}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={isOpen ? 'Colapsar historial' : 'Expandir historial'}
            aria-expanded={isOpen}
          >
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Creada',
      cell: ({ row }) => (
        <span className={row.original._isSubrow ? 'text-muted-foreground' : ''}>
          {formatDateShort(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: 'offer_expires_at',
      header: 'Expira',
      cell: ({ row }) => (
        <span className={row.original._isSubrow ? 'text-muted-foreground' : ''}>
          {formatDateShort(row.original.offer_expires_at)}
        </span>
      ),
    },
    {
      accessorKey: 'full_name',
      header: 'Nombre',
      cell: ({ row }) => {
        const r = row.original;
        if (r._isSubrow) {
          // Sub-fila: indentacion + texto tenue para reforzar la jerarquia
          return (
            <span className="pl-6 text-xs text-muted-foreground">Oferta previa</span>
          );
        }
        const name = r.full_name;
        const display = name && name !== 'N/A' ? name : '—';
        return (
          <span className="inline-flex items-center gap-2">
            <span className="font-medium">{display}</span>
            {r._groupCount > 1 && (
              <span
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
                title={`${r._groupCount} ofertas historicas del cliente`}
              >
                × {r._groupCount}
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'phone_number',
      header: 'Telefono',
      cell: ({ row }) => (
        row.original._isSubrow ? (
          <span className="text-muted-foreground" aria-hidden>—</span>
        ) : (
          <span className="tabular-nums">{row.original.phone_number}</span>
        )
      ),
    },
    {
      accessorKey: 'credit_level',
      header: 'Nivel',
      cell: ({ row }) => {
        const level = row.original.credit_level;
        const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
        return isValid ? (
          <CreditLevelBadge level={level as CreditLevel} size="sm" />
        ) : (
          <span className="text-xs text-muted-foreground">{level}</span>
        );
      },
    },
    {
      id: 'rango_monto',
      header: 'Rango Monto',
      cell: ({ row }) => {
        const min = row.original.min_amount_mxn;
        const max = row.original.max_amount_mxn;
        return (
          <span className="tabular-nums text-xs">
            {formatCurrency(min, 'MXN', 'es-MX')} - {formatCurrency(max, 'MXN', 'es-MX')}
          </span>
        );
      },
    },
    {
      accessorKey: 'available_rates',
      header: 'Plazos',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">
          {formatRatesSummary(row.original.available_rates)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ getValue }) => {
        const status = getValue<string>();
        return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
      },
    },
  ], [expandedGroups]);

  const statusOptions = [
    { value: '', label: 'Todos los estados' },
    ...OFFER_STATUSES.map((s) => ({ value: s, label: s })),
  ];

  const levelOptions = [
    { value: '', label: 'Todos los niveles' },
    ...CREDIT_LEVELS.map((l) => ({ value: l, label: l })),
  ];

  const handleRowClick = (row: OfferRow) => {
    setSelectedOffer({ offerId: row.offer_id, directoryId: row.directory_id });
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Ofertas de Credito" helpKey="offers-list"
          subtitle="Lista de ofertas generadas"
          breadcrumbs={BREADCRUMBS}
        />
        <ListSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader
          title="Ofertas de Credito" helpKey="offers-list"
          subtitle="Lista de ofertas generadas"
          breadcrumbs={BREADCRUMBS}
        />
        <ListError onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Ofertas de Credito" helpKey="offers-list"
        subtitle="Lista de ofertas generadas"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filtrar por estado"
        />
        <Select
          options={levelOptions}
          value={levelFilter}
          onChange={setLevelFilter}
          placeholder="Filtrar por nivel"
        />
      </div>

      <DataTable
        data={rows}
        columns={columns}
        searchPlaceholder="Buscar por telefono o ID de oferta..."
        onRowClick={handleRowClick}
        emptyMessage="No se encontraron ofertas"
        pageSize={10}
      />

      {selectedOffer && (
        <OfferDetailModal
          isOpen
          offerId={selectedOffer.offerId}
          directoryId={selectedOffer.directoryId}
          onClose={() => setSelectedOffer(null)}
        />
      )}
    </div>
  );
}
